import { Link } from 'react-router';
import type { Ticket, TicketList, TicketSeat } from '@gatecheckpro/contracts';
import { EmptyState, LoadingRegion, Notice, formatDateInZone } from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useViewer } from '../lib/session.tsx';
import { isVoid, VoidNotice } from '../lib/ticket-validity.tsx';

/**
 * Owned tickets.
 *
 * One card per ticket, not one per order: four tickets is four rights of
 * admission, each independently presented at a gate and — from a later release —
 * independently transferable. Collapsing them into "Order GC-1234 × 4" would
 * make the common case of walking in with friends impossible to do from this
 * screen.
 *
 * An empty list here now means "you have none". Until Slice 4 it meant "this
 * does not work yet", and the server still distinguishes the two so this page
 * never has to guess which sentence is true.
 */
export function MyTicketsPage() {
  const viewer = useViewer();
  const { resource } = useResource<TicketList>('/api/v1/customer/tickets');

  return (
    <Chrome>
      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '48rem' }}>
          <div className="gc-page-header">
            <span className="gc-eyebrow">{viewer.displayName}</span>
            <h1>My tickets</h1>
          </div>

          {resource.status === 'loading' && <LoadingRegion label="Loading your tickets" />}
          {resource.status === 'failed' && <FailureNotice error={resource.error} />}

          {resource.status === 'loaded' && (
            <>
              {resource.data.tickets.length === 0 ? (
                <EmptyState
                  title="You have no tickets yet"
                  action={
                    <Link to="/" className="gc-button gc-button--primary">
                      See what&rsquo;s on
                    </Link>
                  }
                >
                  Tickets you buy appear here as soon as GateCheck has confirmed the payment with
                  the provider.
                </EmptyState>
              ) : (
                <section className="gc-stack">
                  {resource.data.tickets.map((ticket) => (
                    <article key={ticket.ticketId} className="gc-card">
                      <div className="gc-stack gc-stack--tight">
                        <div className="gc-row gc-row--between gc-row--baseline">
                          <h2 style={{ margin: 0 }}>{ticket.event.title}</h2>
                          <span className="gc-mono gc-muted">{ticket.ticketNumber}</span>
                        </div>
                        <strong>{ticket.ticketName}</strong>
                        {/*
                          The seat facts stay on a void ticket, all of them.
                          What somebody bought, and where they were moved to, is
                          the record they will be asked about if they query the
                          refund — erasing it would leave them arguing from
                          memory.
                        */}
                        <TicketSeatLine seat={ticket.seat} />
                        <SeatChangeNote ticket={ticket} />
                        <VoidNotice ticket={ticket} />
                        {ticket.event.venueName !== null && (
                          <span className="gc-muted">{ticket.event.venueName}</span>
                        )}
                        {ticket.event.startsAt !== null && (
                          <span className="gc-muted">
                            {formatDateInZone(ticket.event.startsAt, ticket.event.timezone)}
                          </span>
                        )}
                        <div className="gc-row">
                          {/*
                            No route to a pass that cannot admit anybody. The
                            pass screen refuses too — this is so nobody is
                            offered the journey in the first place.
                          */}
                          {!isVoid(ticket) && (
                            <Link
                              to={`/tickets/${ticket.ticketId}`}
                              className="gc-button gc-button--primary"
                            >
                              Show pass
                            </Link>
                          )}
                          <Link to={`/e/${ticket.event.slug}`}>About this event</Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </section>
              )}

              <Notice tone="info" title="How your pass works">
                Your pass is a signed code you can show from any browser. It proves the ticket is
                real. It does not stop somebody else using a photo of it, so gate staff catch
                duplicates when they scan — keep it to yourself.
              </Notice>
            </>
          )}
        </div>
      </main>
    </Chrome>
  );
}

/**
 * The chair this ticket is, when it is a chair.
 *
 * Read from the immutable assignment the purchase froze, so it keeps saying the
 * same thing after the organizer renames a section, reprices the room or
 * publishes a new map. Null for general admission, where there is no chair to
 * name and inventing one would be worse than saying nothing.
 */
export function TicketSeatLine({ seat }: { readonly seat: TicketSeat | null }) {
  if (seat === null) return null;
  return (
    <strong data-testid="ticket-seat">
      {[seat.tableName, seat.sectionName, seat.rowLabel, `Seat ${seat.seatLabel}`]
        .filter((part) => part !== null && part !== '')
        .join(' · ')}
    </strong>
  );
}

/**
 * That the organizer moved this ticket, said once and plainly.
 *
 * Shown because the alternative is worse than saying nothing: a ticket that
 * quietly reads as a different chair looks like the customer misremembered
 * their own purchase, or like the seat they paid for was never theirs. Somebody
 * who booked an aisle seat months ago and finds a middle one deserves to know
 * that GateCheckPro moved them, not to wonder whether they made a mistake.
 *
 * Their purchase is named alongside, because it is intact and unchanged. The
 * ticket is still the one they bought; only where they sit has moved.
 *
 * The reason is the one coarse category the server publishes. The organizer's
 * own reason can carry operational or safety meaning — a broken seat, a
 * decision about a particular row — and none of that belongs on a customer's
 * ticket.
 */
export function SeatChangeNote({ ticket }: { readonly ticket: Ticket }) {
  const change = ticket.seatChange;
  const original = ticket.originalSeat;
  if (change === null || original === null) return null;

  return (
    <span className="gc-muted" data-testid="seat-change">
      The organizer moved this ticket
      {change.moveCount > 1 ? ` ${String(change.moveCount)} times` : ''}. You bought{' '}
      {describeSeat(original)}.
    </span>
  );
}

/** A seat in the order a person reads it: the biggest place first. */
function describeSeat(seat: TicketSeat): string {
  return [seat.tableName, seat.sectionName, seat.rowLabel, `seat ${seat.seatLabel}`]
    .filter((part) => part !== null && part !== '')
    .join(', ');
}
